import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

/**
 * Shared state for app header search + Export Excel + primary action (e.g. Add Project, Add Employee).
 * Use on Project Management, Employee Management. Each page sets config on init; clears on destroy.
 */
@Injectable({ providedIn: 'root' })
export class PageHeaderSearchActionService {
  private readonly searchText$ = new BehaviorSubject<string>('');
  private readonly primaryActionClick$ = new Subject<void>();
  private readonly canShowPrimaryAction$ = new BehaviorSubject<boolean>(false);
  private readonly placeholder$ = new BehaviorSubject<string>('');
  private readonly primaryButtonLabel$ = new BehaviorSubject<string>('');
  private readonly exportClick$ = new Subject<void>();
  private readonly canShowExport$ = new BehaviorSubject<boolean>(false);
  private readonly exportDisabled$ = new BehaviorSubject<boolean>(true);

  // Generic filter select (shown in app header next to Year/Month selects)
  private readonly filterSelectOptions$ = new BehaviorSubject<{ label: string; value: any }[]>([]);
  private readonly filterSelectValue$ = new BehaviorSubject<any>(null);
  private readonly filterSelectChange$ = new Subject<any>();
  private readonly filterSelectPlaceholder$ = new BehaviorSubject<string>('All');
  private readonly canShowFilterSelect$ = new BehaviorSubject<boolean>(false);
  private readonly primaryButtonIcon$ = new BehaviorSubject<string>('pi pi-plus');

  // Filter bar visibility toggle (default: hidden)
  private readonly filtersVisible$ = new BehaviorSubject<boolean>(
    localStorage.getItem('filtersVisible') === 'true'
  );
  private readonly canShowFilterToggle$ = new BehaviorSubject<boolean>(false);

  readonly searchText = this.searchText$.asObservable();
  readonly primaryActionClick = this.primaryActionClick$.asObservable();
  readonly canShowPrimaryAction = this.canShowPrimaryAction$.asObservable();
  readonly placeholder = this.placeholder$.asObservable();
  readonly primaryButtonLabel = this.primaryButtonLabel$.asObservable();
  readonly primaryButtonIcon = this.primaryButtonIcon$.asObservable();
  readonly exportClick = this.exportClick$.asObservable();
  readonly canShowExport = this.canShowExport$.asObservable();
  readonly exportDisabled = this.exportDisabled$.asObservable();
  readonly filterSelectOptions = this.filterSelectOptions$.asObservable();
  readonly filterSelectValue = this.filterSelectValue$.asObservable();
  readonly filterSelectChange = this.filterSelectChange$.asObservable();
  readonly filterSelectPlaceholder = this.filterSelectPlaceholder$.asObservable();
  readonly canShowFilterSelect = this.canShowFilterSelect$.asObservable();
  readonly filtersVisible = this.filtersVisible$.asObservable();
  readonly canShowFilterToggle = this.canShowFilterToggle$.asObservable();

  get currentFiltersVisible(): boolean {
    return this.filtersVisible$.value;
  }

  get currentSearchText(): string {
    return this.searchText$.value;
  }

  get currentFilterSelectValue(): any {
    return this.filterSelectValue$.value;
  }

  setSearchText(value: string): void {
    this.searchText$.next(value ?? '');
  }

  triggerPrimaryAction(): void {
    this.primaryActionClick$.next();
  }

  setCanShowPrimaryAction(value: boolean): void {
    this.canShowPrimaryAction$.next(!!value);
  }

  setPlaceholder(value: string): void {
    this.placeholder$.next(value ?? '');
  }

  setPrimaryButtonLabel(value: string): void {
    this.primaryButtonLabel$.next(value ?? '');
  }

  setPrimaryButtonIcon(value: string): void {
    this.primaryButtonIcon$.next(value || 'pi pi-plus');
  }

  triggerExport(): void {
    this.exportClick$.next();
  }

  setCanShowExport(value: boolean): void {
    this.canShowExport$.next(!!value);
  }

  setExportDisabled(value: boolean): void {
    this.exportDisabled$.next(!!value);
  }

  setFilterSelect(options: { label: string; value: any }[], placeholder = 'All'): void {
    this.filterSelectOptions$.next(options);
    this.filterSelectPlaceholder$.next(placeholder);
    this.canShowFilterSelect$.next(true);
  }

  setFilterSelectValue(value: any): void {
    this.filterSelectValue$.next(value);
  }

  triggerFilterSelectChange(value: any): void {
    this.filterSelectValue$.next(value);
    this.filterSelectChange$.next(value);
  }

  toggleFiltersVisible(): void {
    const next = !this.filtersVisible$.value;
    this.filtersVisible$.next(next);
    localStorage.setItem('filtersVisible', String(next));
  }

  setCanShowFilterToggle(value: boolean): void {
    this.canShowFilterToggle$.next(!!value);
  }

  /** Call when a page using the header is destroyed (e.g. on ngOnDestroy). */
  clearPageConfig(): void {
    this.searchText$.next('');
    this.placeholder$.next('');
    this.primaryButtonLabel$.next('');
    this.primaryButtonIcon$.next('pi pi-plus');
    this.setCanShowPrimaryAction(false);
    this.setCanShowExport(false);
    this.setExportDisabled(true);
    this.canShowFilterSelect$.next(false);
    this.filterSelectOptions$.next([]);
    this.filterSelectValue$.next(null);
    this.canShowFilterToggle$.next(false);
  }
}
